// ─── Pricing Calculator (R-Blade Chatflow Logic) ─────────────────────────────
// Pure calculation module — no 3D or DOM dependencies.
// Takes configuration data and returns a structured pricing summary.

const POST_SIZES = {
  '6.5x6.5x12': { label: '6.5" × 6.5" × 12\'', sku: 'POST-6512', unitPrice: 0 },
  '8x8x15':     { label: '8" × 8" × 15\'',      sku: 'POST-8815', unitPrice: 0 },
};

const DEFAULT_POST_SIZE = '6.5x6.5x12';

const BEAM_SKUS = {
  accessory:     { sku: 'BEAM-FAN',      label: 'Accessory Beam (Fan Beam)',       unitPrice: 0 },
  structural:    { sku: 'BEAM-STRUCT',    label: 'Structural Header Beam',          unitPrice: 0 },
  heater:        { sku: 'BEAM-HEATER',    label: 'Integrated Heater Beam',          unitPrice: 0 },
  reinforcement: { sku: 'BEAM-REINFORCE', label: 'Reinforcement (Pass-Through)',    unitPrice: 0 },
};

export class PricingCalculator {
  constructor() {
    this.lastSummary = null;
  }

  // ─── Bay breakdown (R-Blade Gate 3 logic) ────────────────────────────────
  getBayBreakdown(widthInches, lengthInches) {
    // Round up to next foot
    const widthFt = Math.ceil(widthInches / 12);
    const lengthFt = Math.ceil(lengthInches / 12);

    const widthBays = Math.ceil(widthFt / 16);
    const lengthBays = Math.ceil(lengthFt / 23);
    const totalBays = widthBays * lengthBays;

    // Per-bay dimensions (rounded up)
    const bayWidth = Math.ceil(widthFt / widthBays);
    const bayLength = Math.ceil(lengthFt / lengthBays);

    return {
      widthFt,
      lengthFt,
      widthBays,
      lengthBays,
      totalBays,
      bayWidth,
      bayLength,
      isMultiBay: totalBays > 1,
    };
  }

  // ─── Multi-bay structural packages (R-Blade Gate 3 step 4) ───────────────
  getMultiBayPackages(bayBreakdown) {
    const packages = [];

    if (bayBreakdown.totalBays > 1) {
      packages.push({
        item: 'Beam-to-Beam Cover',
        qty: bayBreakdown.totalBays - 1,
        sku: 'B2B-COVER',
        unitPrice: 0,
      });

      if (bayBreakdown.lengthBays > 1) {
        packages.push({
          item: 'Pass-Through Gutter Reinforcement',
          qty: bayBreakdown.widthBays * (bayBreakdown.lengthBays - 1),
          sku: 'PTGR',
          unitPrice: 0,
        });
      }
    }

    return packages;
  }

  // ─── Post pricing line item ──────────────────────────────────────────────
  getPostLineItem(postCount, postSizeKey = DEFAULT_POST_SIZE) {
    const size = POST_SIZES[postSizeKey] || POST_SIZES[DEFAULT_POST_SIZE];
    return {
      item: `Post — ${size.label}`,
      qty: postCount,
      sku: size.sku,
      unitPrice: size.unitPrice,
      total: postCount * size.unitPrice,
    };
  }

  // ─── Beam line items ──────────────────────────────────────────────────────
  getBeamLineItems(beamCounts = {}) {
    const items = [];
    for (const [type, qty] of Object.entries(beamCounts)) {
      const beamInfo = BEAM_SKUS[type];
      if (beamInfo && qty > 0) {
        items.push({
          item: beamInfo.label,
          qty,
          sku: beamInfo.sku,
          unitPrice: beamInfo.unitPrice,
          total: qty * beamInfo.unitPrice,
        });
      }
    }
    return items;
  }

  // ─── Full pricing summary ────────────────────────────────────────────────
  calculate(config) {
    const {
      widthInches,
      lengthInches,
      structureType,
      postCount,
      cornerPostCount = postCount,
      junctionPostCount = 0,
      postSize = DEFAULT_POST_SIZE,
      beamCounts = {},
      lightingLineItems = [],
      zones = null,
      scupperCount = 0,
      beamToBeamCovers = 0,
    } = config;

    const bayBreakdown = this.getBayBreakdown(widthInches, lengthInches);
    const multiBayPackages = this.getMultiBayPackages(bayBreakdown);
    const postLineItem = this.getPostLineItem(postCount, postSize);
    const beamLineItems = this.getBeamLineItems(beamCounts);

    // Zone-aware bay line items
    const bayLineItems = [];
    if (zones && zones.zones && zones.zones.length > 1) {
      // Multi-zone: per-zone SKUs with roof type
      for (const z of zones.zones) {
        const roofLabel = z.roofType === 'r-blade' ? 'R-Blade'
          : z.roofType === 'r-shade' ? 'R-Shade' : 'R-Breeze';
        const wFt = Math.ceil(z.widthInches / 12);
        const lFt = Math.ceil(z.lengthInches / 12);
        bayLineItems.push({
          item: `${roofLabel} Zone ${wFt}' × ${lFt}'`,
          qty: 1,
          sku: `${z.roofType.toUpperCase().replace('-', '')}-${wFt}x${lFt}`,
          unitPrice: 0,
          note: 'SKU lookup pending',
        });
      }
    } else {
      // Single zone: standard bay line items
      for (let i = 0; i < bayBreakdown.totalBays; i++) {
        bayLineItems.push({
          item: `R-Blade Bay ${bayBreakdown.bayWidth}' × ${bayBreakdown.bayLength}'`,
          qty: 1,
          sku: `RBLADE-${bayBreakdown.bayWidth}x${bayBreakdown.bayLength}`,
          unitPrice: 0,
          note: 'SKU lookup pending — requires products.json',
        });
      }
    }

    // Zone-specific structural items
    const zoneStructuralItems = [];
    if (junctionPostCount > 0) {
      zoneStructuralItems.push({
        item: 'Junction Post',
        qty: junctionPostCount,
        sku: 'POST-JUNCTION',
        unitPrice: 0,
      });
    }
    if (scupperCount > 0) {
      zoneStructuralItems.push({
        item: 'Scupper (Drainage)',
        qty: scupperCount,
        sku: 'SCUPPER',
        unitPrice: 0,
      });
    }
    if (beamToBeamCovers > 0) {
      zoneStructuralItems.push({
        item: 'Beam-to-Beam Cover',
        qty: beamToBeamCovers,
        sku: 'B2B-COVER',
        unitPrice: 0,
      });
    }

    this.lastSummary = {
      structureType,
      bayBreakdown,
      postCount,
      cornerPostCount,
      junctionPostCount,
      postSize: (POST_SIZES[postSize] || POST_SIZES[DEFAULT_POST_SIZE]).label,
      lineItems: [
        ...bayLineItems,
        postLineItem,
        ...zoneStructuralItems,
        ...multiBayPackages.filter(p => beamToBeamCovers === 0 || p.sku !== 'B2B-COVER'),
        ...beamLineItems,
        ...lightingLineItems,
      ],
      specialCuts: [],
    };

    return this.lastSummary;
  }

  getSummary() {
    return this.lastSummary;
  }
}
